import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { useKnowledgeEmbeddings } from "@/hooks/useKnowledgeEmbeddings";

export interface KnowledgeArticleEditorProps {
  companyId: string;
  collectionId: string;
  articleId: string | null;
  isCreating: boolean;
  onCancel: () => void;
  onSave: (article?: any) => void;
}

export function KnowledgeArticleEditor({
  companyId,
  collectionId,
  articleId,
  isCreating,
  onCancel,
  onSave
}: KnowledgeArticleEditorProps) {
  const [title, setTitle] = useState("");
  const [editorContent, setEditorContent] = useState("");
  const [publishState, setPublishState] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { generateEmbeddings, isGenerating } = useKnowledgeEmbeddings();

  useEffect(() => {
    if (articleId && !isCreating) {
      fetchArticle();
    }
  }, [articleId, isCreating]);

  const fetchArticle = async () => {
    if (!articleId) return;
    
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("knowledge_articles")
        .select("*")
        .eq("id", articleId)
        .single();
      
      if (error) throw error;
      
      if (data) {
        setTitle(data.title);
        setEditorContent(data.content);
        setPublishState(data.is_published);
      }
    } catch (error) {
      console.error("Error fetching article:", error);
      toast({
        title: "Error",
        description: "Failed to load article",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setEditorContent("");
    setPublishState(true);
  };

  const handleCreateArticle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title) {
      toast({
        title: "Error",
        description: "Title is required",
        variant: "destructive"
      });
      return;
    }
    
    if (!editorContent) {
      toast({
        title: "Error", 
        description: "Content is required",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Generate slug from title
      const generatedSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      const { data: existingArticle, error: checkError } = await supabase
        .from("knowledge_articles")
        .select("id")
        .eq("collection_id", collectionId)
        .eq("slug", generatedSlug)
        .maybeSingle();
      
      if (checkError) throw checkError;
      
      if (existingArticle) {
        toast({
          title: "Error",
          description: "An article with this title already exists",
          variant: "destructive"
        });
        return;
      }
      
      // Insert the new article with all required fields
      const { data: article, error } = await supabase
        .from("knowledge_articles")
        .insert({
          title: title,
          content: editorContent,  // Ensure content is provided
          slug: generatedSlug,
          company_id: companyId,
          collection_id: collectionId,
          is_published: publishState
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Generate embeddings for the new article
      if (article) {
        await generateEmbeddings(article.id, companyId);
      }
      
      // Handle successful creation
      toast({
        title: "Success",
        description: "Knowledge article created successfully"
      });
      resetForm();
      onSave(article);
      
    } catch (error) {
      console.error("Error creating article:", error);
      toast({
        title: "Error",
        description: "Failed to create article",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateArticle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title) {
      toast({
        title: "Error",
        description: "Title is required",
        variant: "destructive"
      });
      return;
    }
    
    if (!editorContent) {
      toast({
        title: "Error",
        description: "Content is required",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsLoading(true);
      
      const { data: article, error } = await supabase
        .from("knowledge_articles")
        .update({
          title: title,
          content: editorContent,
          is_published: publishState,
          updated_at: new Date().toISOString()
        })
        .eq("id", articleId)
        .select()
        .single();
      
      if (error) throw error;
      
      // Generate embeddings for the updated article
      if (article) {
        await generateEmbeddings(article.id, companyId);
      }
      
      toast({
        title: "Success",
        description: "Knowledge article updated successfully"
      });
      onSave(article);
    } catch (error) {
      console.error("Error updating article:", error);
      toast({
        title: "Error",
        description: "Failed to update article",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteArticle = async () => {
    if (!articleId) return;
    
    const confirmed = window.confirm("Are you sure you want to delete this article?");
    if (!confirmed) return;
    
    try {
      setIsLoading(true);
      
      const { error } = await supabase
        .from("knowledge_articles")
        .delete()
        .eq("id", articleId);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Knowledge article deleted successfully"
      });
      onSave({ id: articleId, deleted: true });
    } catch (error) {
      console.error("Error deleting article:", error);
      toast({
        title: "Error",
        description: "Failed to delete article",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-medium">
          {isCreating ? "Create New Article" : "Edit Article"}
        </h3>
        <div className="flex space-x-2">
          {!isCreating && (
            <Button
              variant="destructive"
              onClick={handleDeleteArticle}
              disabled={isLoading || isGenerating}
            >
              Delete
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isLoading || isGenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={isCreating ? handleCreateArticle : handleUpdateArticle}
            disabled={isLoading || isGenerating}
          >
            {isLoading || isGenerating ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <form className="space-y-4">
        <div>
          <label htmlFor="title" className="block text-sm font-medium mb-1">
            Title
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full p-2 border rounded-md"
            placeholder="Article title"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Content
          </label>
          <RichTextEditor 
            value={editorContent} 
            onChange={setEditorContent}
            placeholder="Write your article content here..."
          />
        </div>

        <div className="flex items-center space-x-2">
          <input
            id="publish"
            type="checkbox"
            checked={publishState}
            onChange={(e) => setPublishState(e.target.checked)}
            className="h-4 w-4"
          />
          <label htmlFor="publish" className="text-sm font-medium">
            Publish article
          </label>
        </div>
      </form>
    </div>
  );
}
